import TiptapEditor from '@/components/Editor/TiptapEditor';
import { stripS3ImageUrls } from '@/components/Editor/extensions/S3Image';
import { useAuth } from "@/context/AuthContext";
import { useGlobalLoading } from "@/context/GlobalLoadingContext";
import { useDebounce } from '@/hooks/useDebounce';
import { useEffect, useState, useRef } from 'react';

export default function TaskDescription({ task, onTaskChange, editorRef, isLoading }) {


    // Early return loading state
    if (isLoading || !task) {
        return (
            <div className="py-4 space-y-4">
                <div className="h-5 bg-muted animate-pulse rounded-md w-3/4" />
                <div className="h-5 bg-muted animate-pulse rounded-md w-full" />
                <div className="h-5 bg-muted animate-pulse rounded-md w-2/3" />
                <div className="h-5 bg-muted animate-pulse rounded-md w-1/2" />
            </div>
        );
    }

    const { axiosInstance } = useAuth();
    const { showLoading, hideLoading } = useGlobalLoading();

    const [description, setDescription] = useState(task?.description || null);
    const debouncedDescription = useDebounce(description, 1000);
    const pendingSaveRef = useRef(false);

    const handleDescriptionUpdate = async (newContent) => {
        try {
            // Strip S3 signed URLs before saving - they expire and shouldn't be stored
            const contentToSave = stripS3ImageUrls(structuredClone(newContent));
            const { data: updatedTask } = await axiosInstance.patch(
                `/tasks/${task.organization.slug}/${task.key}`, 
                { description: contentToSave }
            );
            onTaskChange(updatedTask);
        } catch (error) {
            console.error('Failed to update description:', error);
            setDescription(task.description);
        } finally {
            hideLoading();
            pendingSaveRef.current = false;
        }
    };

    useEffect(() => {
        if (
            debouncedDescription && 
            JSON.stringify(task?.description) !== JSON.stringify(debouncedDescription)
        ) {
            handleDescriptionUpdate(debouncedDescription);
        } else if (pendingSaveRef.current && JSON.stringify(debouncedDescription) === JSON.stringify(task?.description)) {
            // User typed but value matches original - no save needed
            hideLoading();
            pendingSaveRef.current = false;
        }
    }, [debouncedDescription]);

    return (
        <div>
            <TiptapEditor
                ref={editorRef}
                content={description}
                placeholder="Add description..."
                withMentions
                withCodeBlocks
                withBubbleMenu
                withS3Images
                withS3Uploads
                organizationSlug={task.organization.slug}
                taskKey={task.key}
                onTaskChange={onTaskChange}
                onUpdate={({ editor }) => {
                    const json = editor.getJSON();
                    setDescription(json);
                    
                    // Show loading immediately when user types (if not already showing)
                    if (!pendingSaveRef.current && JSON.stringify(json) !== JSON.stringify(task?.description)) {
                        showLoading();
                        pendingSaveRef.current = true;
                    }
                }}
                onEditorReady={(editor) => {
                    editorRef.current = editor;
                }}
            />
        </div>
    );
}